import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import {
  getSearchEnv,
  listSearchEnvs,
  SearchEnv,
  SearchEnvStatus,
  TERMINAL_SEARCH_ENV_STATUSES,
} from "../../api/searchEnvs";
import { WorkflowDetail } from "../../api/workflows";
import { MeResponse } from "../../api/me";
import { costForWorkflow, formatCost } from "../../api/cost";
import { CollapsibleSection } from "../CollapsibleSection";

// Aggregate cost view for an entire dataset: workflow compute (from
// pod cgroup counters) + every search env's incurred/projected cost.
// Renders at the bottom of /exports/<id> so a user can see total
// spend in one glance instead of summing across the per-env cards in
// the Customer Search section.
//
// Search-env cost fields are only populated on GET-one (not on the
// list endpoint), so we list-then-fetch-per-env. Small N (typical
// workflow has < 5 historical envs), so the request waterfall is
// brief and runs lazily once the user expands the section.

interface Props {
  wf: WorkflowDetail;
  me: MeResponse | null;
}

interface EnvCost {
  env: SearchEnv;
  incurred: number;
  projected: number;
}

export function CostSummaryPanel({ wf, me }: Props) {
  const [envCosts, setEnvCosts] = useState<EnvCost[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSearchEnvs({ workflow_id: wf.id })
      .then(async (r) => {
        // list endpoint omits cost fields; fan out to the detail
        // endpoint for each env. Parallel — N is small.
        const detailed = await Promise.all(
          r.search_envs.map((e) => getSearchEnv(e.id)),
        );
        if (cancelled) return;
        setEnvCosts(
          detailed.map((env) => ({
            env,
            incurred: env.incurred_usd ?? 0,
            projected: env.projected_usd ?? 0,
          })),
        );
      })
      .catch((err) => {
        if (!cancelled) setError(String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [wf.id]);

  const rates = me
    ? { cpuHrUsd: me.cost_cpu_hr_usd, memGibHrUsd: me.cost_mem_gib_hr_usd }
    : null;
  const computeCost = rates ? costForWorkflow(wf.jobs, rates) : null;

  // Dataset compute is a sunk cost once the export finishes — it's
  // both the "so far" and the "projected" contribution. Search-env
  // projected and incurred can diverge (env is still live + accruing
  // domain-hour cost up to its TTL).
  const computeBoth = computeCost ?? 0;
  const totalIncurred =
    computeBoth + (envCosts?.reduce((acc, e) => acc + e.incurred, 0) ?? 0);
  const totalProjected =
    computeBoth + (envCosts?.reduce((acc, e) => acc + e.projected, 0) ?? 0);

  // Cost is admin-facing. Non-admins only see it once a dataset's projected
  // total clears $200 — and only after env costs resolve (compute alone
  // rarely tells the whole story; unknown/errored is treated as ≤ $200, so
  // it stays hidden). Admins always see it. The section chrome lives here
  // (not in the parent) so the whole thing — header included — disappears
  // when hidden, and the panel still mounts to compute the gate.
  const isAdmin = me?.role === "admin";
  const costVisible = isAdmin || (envCosts !== null && totalProjected > 200);
  if (!costVisible) return null;

  const content = (
    <Stack spacing={2}>
      <CostLine
        label="Dataset compute"
        sublabel="Fargate pod-hours that produced the export"
        incurred={computeCost}
        projected={computeCost}
      />

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Search environments
        </Typography>
        {envCosts === null && !error && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              fetching env costs…
            </Typography>
          </Stack>
        )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
        {envCosts && envCosts.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            (no search environments have been provisioned for this dataset)
          </Typography>
        )}
        {envCosts &&
          envCosts.length > 0 &&
          envCosts.map((ec) => (
            <CostLine
              key={ec.env.id}
              label={
                <MuiLink
                  component={RouterLink}
                  to={`/search-envs/${ec.env.id}`}
                  underline="hover"
                >
                  <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                    {ec.env.id}
                  </Typography>
                </MuiLink>
              }
              sublabel={`${ec.env.status}${
                ec.env.instance_class
                  ? ` · ${ec.env.instance_class} × ${ec.env.instance_count ?? 1}`
                  : ""
              }`}
              incurred={ec.incurred}
              projected={
                TERMINAL_SEARCH_ENV_STATUSES.has(
                  ec.env.status as SearchEnvStatus,
                )
                  ? ec.incurred
                  : ec.projected
              }
              terminal={TERMINAL_SEARCH_ENV_STATUSES.has(
                ec.env.status as SearchEnvStatus,
              )}
            />
          ))}
      </Box>

      <Box
        sx={{
          pt: 1.5,
          borderTop: "2px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Total
          </Typography>
          <Stack direction="row" spacing={2}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCost(totalIncurred)}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                so far
              </Typography>
            </Typography>
            {Math.abs(totalProjected - totalIncurred) > 0.005 && (
              <Typography variant="body1" color="text.secondary">
                {formatCost(totalProjected)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 0.5 }}
                >
                  projected
                </Typography>
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <CollapsibleSection
      title="Cost Summary"
      subtitle="dataset compute + every search env, incurred & projected"
    >
      {content}
    </CollapsibleSection>
  );
}

function CostLine({
  label,
  sublabel,
  incurred,
  projected,
  terminal,
}: {
  label: React.ReactNode;
  sublabel?: string;
  incurred: number | null;
  projected: number | null;
  terminal?: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      sx={{ py: 0.5 }}
    >
      <Box>
        <Typography variant="body2" component="div">
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" component="div">
            {sublabel}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={2} alignItems="baseline">
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {incurred !== null ? formatCost(incurred) : "—"}
        </Typography>
        {projected !== null &&
          incurred !== null &&
          Math.abs(projected - incurred) > 0.005 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              → {formatCost(projected)}{terminal ? "" : " projected"}
            </Typography>
          )}
      </Stack>
    </Stack>
  );
}
